import React from 'react';
import { useParams, Link } from 'react-router-dom';
import MenuBar from '../MenuBar';
import Sidebar from '../Sidebar';
import Footer from '../Footer';
import PageBanner from '../PageBanner';
import DepartmentsData, { getDepartmentBySlug } from './DepartmentsData';

// Breadcrumb trail shared by every department detail banner
const DEPARTMENT_CRUMBS = [{ to: '/USA/departments', label: 'Departments' }];

const DepartmentDetailPage = () => {
  const { slug } = useParams();
  const dept = getDepartmentBySlug(slug);

  if (!dept) {
    return (
      <>
        <MenuBar />
        <Sidebar />
        <PageBanner title="Department Not Found" current="Not Found" crumbs={DEPARTMENT_CRUMBS} />
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <i className="fas fa-exclamation-circle" style={{ fontSize: '48px', color: '#CBD5E1', display: 'block', marginBottom: '16px' }}></i>
          <p style={{ color: '#64748B', fontSize: '18px', marginBottom: '24px' }}>
            The department you're looking for doesn't exist.
          </p>
          <Link
            to="/USA/departments"
            style={{
              padding: '12px 28px', borderRadius: '100px',
              background: 'linear-gradient(135deg,#5B5FEF,#9F67FF)',
              color: '#fff', textDecoration: 'none', fontWeight: 700,
            }}
          >
            ← Back to All Departments
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  const idx  = DepartmentsData.findIndex(d => d.id === dept.id);
  const prev = idx > 0 ? DepartmentsData[idx - 1] : null;
  const next = idx < DepartmentsData.length - 1 ? DepartmentsData[idx + 1] : null;
  const isPublic = dept.type === 'Public Facing';

  const parseTeamMember = (member) => {
    const nameMatch = member.match(/^([^(]+)/);
    const roleMatch = member.match(/\(([^)]+)\)/);
    return {
      name: nameMatch ? nameMatch[1].trim() : member,
      role: roleMatch ? roleMatch[1] : 'Member',
    };
  };

  return (
    <>
      <MenuBar />
      <Sidebar />

      {/* Page Banner */}
      <PageBanner title={dept.name} crumbs={DEPARTMENT_CRUMBS} />

      {/* Detail Content */}
      <div
        className="dept-detail-page"
        style={{ '--dept-gradient': dept.gradient }}
      >
        {/* Background blobs */}
        <div className="dept-section__blob dept-section__blob--1"></div>
        <div className="dept-section__blob dept-section__blob--2"></div>

        <div className="auto-container" style={{ paddingTop: '56px', position: 'relative', zIndex: 1 }}>

          {/* Hero Card */}
          <div className="ddp-hero" style={{ '--dept-gradient': dept.gradient }}>
            <div className="d-flex align-items-start gap-4 flex-wrap">
              <div className="ddp-hero__icon">
                <i className={`fas ${dept.icon}`}></i>
              </div>
              <div className="flex-1" style={{ flex: 1, minWidth: '200px' }}>
                <div className="d-flex align-items-center gap-3 flex-wrap mb-2">
                  <h1 className="ddp-hero__name mb-0">{dept.name}</h1>
                  <span className={`ddp-hero__badge ${isPublic ? 'is-public' : 'is-private'}`}>
                    <span className="type-dot"></span>
                    {dept.type}
                  </span>
                </div>
                <p className="ddp-hero__about">{dept.about}</p>
              </div>
            </div>

            {/* Highlight Stats */}
            <div className="ddp-highlights">
              {dept.highlights.map(h => (
                <div key={h.label} className="ddp-stat" style={{ '--dept-gradient': dept.gradient }}>
                  <div className="ddp-stat__value">{h.value}</div>
                  <div className="ddp-stat__label">{h.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Main 2-col Layout */}
          <div className="row g-4">

            {/* Left: Activities */}
            <div className="col-lg-8">
              <div className="ddp-card" style={{ '--dept-gradient': dept.gradient }}>
                <div className="ddp-card__heading">
                  <div className="heading-icon"><i className="fas fa-bolt"></i></div>
                  <h3>Activities &amp; Programs</h3>
                </div>
                <div className="ddp-activities">
                  {dept.activities.map((activity, i) => (
                    <div key={i} className="ddp-activity-item">
                      <span className="act-dot"></span>
                      <span>{activity}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Sidebar */}
            <div className="col-lg-4">

              {/* Coordinator Card */}
              <div className="ddp-card" style={{ '--dept-gradient': dept.gradient }}>
                <div className="ddp-card__heading">
                  <div className="heading-icon"><i className="fas fa-user"></i></div>
                  <h3>Coordinator</h3>
                </div>
                <p className="ddp-coord-name" style={{ '--dept-gradient': dept.gradient }}>{dept.coordinator}</p>
                <p style={{ fontSize: '13px', color: '#94A3B8', margin: 0 }}>Department Lead</p>
              </div>

              {/* Schedule & Location Card */}
              <div className="ddp-card" style={{ '--dept-gradient': dept.gradient }}>
                <div className="ddp-card__heading">
                  <div className="heading-icon"><i className="fas fa-clock"></i></div>
                  <h3>Schedule</h3>
                </div>
                <div className="ddp-info-row">
                  <i className="fas fa-calendar-alt"></i>
                  <span>{dept.schedule}</span>
                </div>
                <div className="ddp-info-row">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>{dept.location}</span>
                </div>
              </div>

              {/* Contact Card */}
              <div className="ddp-card" style={{ '--dept-gradient': dept.gradient }}>
                <div className="ddp-card__heading">
                  <div className="heading-icon"><i className="fas fa-envelope"></i></div>
                  <h3>Contact</h3>
                </div>
                <div className="ddp-info-row">
                  <i className="fas fa-envelope"></i>
                  <a href={`mailto:${dept.contact.email}`}>{dept.contact.email}</a>
                </div>
                <div className="ddp-info-row">
                  <i className="fas fa-phone-alt"></i>
                  <a href={`tel:${dept.contact.phone}`}>{dept.contact.phone}</a>
                </div>
              </div>

            </div>
          </div>

          {/* Team Members */}
          <div className="ddp-card mt-2" style={{ '--dept-gradient': dept.gradient }}>
            <div className="ddp-card__heading">
              <div className="heading-icon"><i className="fas fa-users"></i></div>
              <h3>Team Members</h3>
            </div>
            <div className="ddp-team-grid">
              {dept.teamMembers.map((member, i) => {
                const { name, role } = parseTeamMember(member);
                return (
                  <div key={i} className="ddp-member" style={{ '--dept-gradient': dept.gradient }}>
                    <div className="ddp-member__avatar">{name.charAt(0)}</div>
                    <div>
                      <p className="ddp-member__name mb-0">{name}</p>
                      <p className="ddp-member__role mb-0">{role}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Prev / Next Navigation */}
          <div className="ddp-nav">
            {prev ? (
              <Link to={`/USA/departments/${prev.slug}`} className="ddp-nav__btn">
                <i className="fas fa-arrow-left"></i>
                <div>
                  <p className="nav-label">Previous</p>
                  <p className="nav-name">{prev.name}</p>
                </div>
              </Link>
            ) : <div style={{ flex: 1 }}></div>}

            <Link to="/USA/departments" className="ddp-nav__all">
              <i className="fas fa-th-large"></i>
              All Departments
            </Link>

            {next ? (
              <Link to={`/USA/departments/${next.slug}`} className="ddp-nav__btn is-next">
                <i className="fas fa-arrow-right"></i>
                <div>
                  <p className="nav-label">Next</p>
                  <p className="nav-name">{next.name}</p>
                </div>
              </Link>
            ) : <div style={{ flex: 1 }}></div>}
          </div>

        </div>
      </div>

      <Footer />
    </>
  );
};

export default DepartmentDetailPage;
